import { ReactNode, useState } from 'react'
import { Theme } from '@mui/material'
import type { SxProps } from '@mui/system'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import dayjs from 'dayjs'
import { DialogCloseButton, UserAvatar } from 'components'
import { Accordion, AccordionSummary, AccordionDetails, Grid, Button, Dialog, DialogTitle, DialogContent } from '@mui/material'
import { ExpandMore } from '@mui/icons-material'
import { Column } from '@material-table/core'

export type AccordionConfig = {
  summaryColumns: string[]
  detailsColumns: string[]
  columns?: Column<Record<string, unknown>>[]
  renderField?: (field: string, value: any, theme?: Theme, row?: any) => ReactNode
  style?: SxProps<Theme>
  showActionMenu?: boolean
  customDetailsRenderer?: (row: any, config: AccordionConfig, classes: any) => ReactNode
  customActions?: (row: any) => ReactNode
  disabledActions?: string[]
}

export const configs: { [key: string]: AccordionConfig } = {
  companies: {
    summaryColumns: ['id', 'name'],
    detailsColumns: [
      'allow_internationalization',
      'notification',
      'enable_rewards_store',
      'beheard_application',
      'beseen_application',
      'insights_calculations_flipper'
    ],
    showActionMenu: true,
    renderField: (field: string, value: any, theme?: Theme) => {
      switch (field) {
        case 'allow_internationalization':
        case 'notification':
        case 'enable_rewards_store':
        case 'beheard_application':
        case 'beseen_application':
        case 'insights_calculations_flipper':
          return (
            <Typography
              fontWeight={600}
              sx={{
                color: value
                  ? theme?.palette?.primary.main
                  : theme?.palette?.error.light
              }}
            >
              {value ? 'Yes' : 'No'}
            </Typography>
          )
        default:
          return value
      }
    },
    style: {
      backgroundColor: '#f5f5f5',
      '& .MuiAccordionSummary-root': {
        backgroundColor: '#ffffff',
      },
    },
  },
  budget: {
    summaryColumns: ['brand'],
    detailsColumns: ['amount', 'vendor_payment_status', 'status'],
    showActionMenu: true,
    renderField: (field, value) => {
      return value
    }
  },
  roles: {
    summaryColumns: ['name'],
    detailsColumns: ['description', 'permissions', 'type'],
    showActionMenu: true,
    renderField: (field, value) => value
  },
  employees: {
    summaryColumns: ['avatar', 'full_name'],
    detailsColumns: ['email', 'title', 'phone_number', 'cell_number', 'roles', 'department', 'hired_at'],
    showActionMenu: true,
    renderField: (field: any, value: any, row: any) => {
      switch (field) {
        case 'roles':
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {row.roles.map((role) => (
                <Chip key={role} label={role} size="small" />
              ))}
            </Box>
          );
        case 'hire_date':
          return dayjs(value).format('MM/DD/YYYY')
        default:
          return value
      }
    },
    customDetailsRenderer: (row: any, config: AccordionConfig, classes: any) => {
      const demographicsMap = (data: any) => {
        const demographics: { [key: string]: string } = {}
        data?.demographics?.forEach((item: any) => {
          demographics[item.name] = item.value
        })
        return demographics
      }

      const demographics = demographicsMap(row)
      const hasDemographics = Object.keys(demographics).length > 0

      return (
        <>
          {config.detailsColumns.map((field: string, colIndex: number) => {
            const column = config.columns?.find(col => col.field === field)
            if (!column) return null

            const renderedValue = config.renderField
              ? config.renderField(field, row[field], row)
              : row[field]

            return (
              <Box key={colIndex} className={classes.detailsField}>
                <Typography variant="subtitle2" color="textSecondary">
                  {column.title}
                </Typography>
                {renderedValue}
              </Box>
            )
          })}

          {hasDemographics && (
            <Accordion className={classes.accordion} sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1">Demographics</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {Object.entries(demographics).map(([key, value], index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box className={classes.detailsField}>
                        <Typography variant="subtitle2" color="textSecondary">
                          {key.split('_').map(word =>
                            word.charAt(0).toUpperCase() + word.slice(1)
                          ).join(' ')}
                        </Typography>
                        <Typography variant="body1">{value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )
    }
  },
  recognitionEvents: {
    summaryColumns: ['title', 'from_user'],
    detailsColumns: ['message', 'rewards', 'to_users'],
    showActionMenu: true,
    renderField: (field, value) => {
      switch (field) {
        case 'rewards':
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {value.map((reward: any) => (
                <Chip
                  key={reward.id}
                  label={`${reward.type}: ${reward.amount}`}
                  size="small"
                />
              ))}
            </Box>
          )
        default:
          return value
      }
    }
  },
  coreValues: {
    summaryColumns: ['name'],
    detailsColumns: ['description', 'examples'],
    showActionMenu: true,
    renderField: (field, value) => {
      if (field === 'examples') {
        return (
          <List>
            {value.map((example: string, index: number) => (
              <ListItem key={index}>
                <ListItemText primary={example} />
              </ListItem>
            ))}
          </List>
        )
      }
      return value
    }
  },
  shareReports: {
    summaryColumns: ['name'],
    detailsColumns: ['close_at', 'engagement_percentage', 'enps_percentage', 'participation_rate'],
    showActionMenu: true,
    renderField: (field, value) => {
      switch (field) {
        case 'close_at':
          return value ? dayjs(value).format('MM/DD/YYYY') : 'N/A'
        default:
          return value
      }
    }
  },
  demographics: {
    summaryColumns: ['name', 'type'],
    detailsColumns: [],
    showActionMenu: true,
    renderField: (field, value) => {
      if (field === 'options') {
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {value.map((option: string) => (
              <Chip key={option} label={option} size="small" />
            ))}
          </Box>
        )
      }
      return value
    }
  },
  'top-active-users': {
    summaryColumns: ['user_avatar', 'user_name'],
    detailsColumns: ['recognitions_sent', 'messages_sent', 'comments_posted', 'total'],
    showActionMenu: false,
    renderField: (field, value) => {
      switch (field) {
        case 'user_avatar':
          return (
            <UserAvatar
              name={'user_name'}
              size={32}
            />
          )
        default:
          return value
      }
    }
  },
  'number-of-recognitions-sent': {
    summaryColumns: ['name'],
    detailsColumns: ['last_recognition_sent', 'manager', 'recognitions_received_since_hired_count', 'role'],
    showActionMenu: false,
    renderField: (field, value) => {
      return value
    }
  },
  'recognition-event-summary': {
    summaryColumns: ['event_name'],
    detailsColumns: ['event_total_recognitions'],
    showActionMenu: false,
    renderField: (field, value) => {
      return value
    }
  },
  'recognition-event-details': {
    summaryColumns: ['recognition_event'],
    detailsColumns: [
      'recognition_date',
      'sender_department',
      'sender_manager_name',
      'recognition_sender_name',
      'recognition_recipient_name',
      'message',
      'answer'
    ],
    showActionMenu: false,
    renderField: (field, value) => {
      switch (field) {
        case 'recognition_date':
          return (
            value ? dayjs(value).format('MM/DD/YYYY') : 'N/A'
          )
        default:
          return value
      }
    }
  },
  'budget-summary-report': {
    summaryColumns: ['label', 'amount'],
    detailsColumns: [],
    showActionMenu: false,
    renderField: (field, value, theme) => {
      if (field === 'amount') {
        const numericValue = parseFloat(value)
        const isNegative = numericValue < 0

        return (
          <Typography
            fontWeight={600}
            color={isNegative ? theme?.palette.error.main : theme?.palette.success.main}
          >
            {numericValue.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </Typography>
        )
      }
      return value
    },
    customDetailsRenderer: () => {
      return null
    }
  },
  'budget-report': {
    summaryColumns: ['date', 'transaction_type'],
    detailsColumns: ['description', 'credit', 'debit', 'running_balance'],
    showActionMenu: false,
    renderField: (field, value) => {
      switch (field) {
        case 'date':
          return (
            value ? dayjs(value).format('MM/DD/YYYY') : 'N/A'
          )
        default:
          return value
      }
    }
  },
  'financial-report': {
    summaryColumns: ['type', 'event_name'],
    detailsColumns: [
      'amount',
      'sender_name',
      'sender_avatar',
      'recipient_name',
      'recipient_avatar',
      'recipient_location',
      'recipient_department',
      'created_at'
    ],
    showActionMenu: false,
    renderField: (field, value) => {
      switch (field) {
        case 'sender_avatar':
        case 'recipient_avatar':
          return (
            <UserAvatar
              name={'user_name'}
              size={32}
            />
          )
        case 'created_at':
          return (
            value ? dayjs(value).format('MM/DD/YYYY') : 'N/A'
          )
        default:
          return value
      }
    }
  },
  'profile-report': {
    summaryColumns: ['user_avatar', 'user_name'],
    detailsColumns: ['status', 'role_name', 'style_name'],
    showActionMenu: false,
    renderField: (field, value) => {
      switch (field) {
        case 'user_avatar':
          return (
            <UserAvatar
              name={'user_name'}
              size={32}
            />
          )
        default:
          return value
      }
    }
  },
  'recognition-and-reward-history': {
    summaryColumns: ['date', 'event'],
    detailsColumns: [
      'department',
      'manager',
      'sender',
      'recipient',
      'message',
      'reward_amount',
      'banked'
    ],
    showActionMenu: false,
    renderField: (field, value) => {
      switch (field) {
        case 'date':
          return (
            value ? dayjs(value).format('MM/DD/YYYY') : 'N/A'
          )
        default:
          return value
      }
    }
  },
  onboardingQuestions: {
    summaryColumns: ['statement'],
    detailsColumns: ['type', 'required'],
    showActionMenu: true,
    renderField: (field, value) => {
      return value
    }
  },
  profileQuestions: {
    summaryColumns: ['statement'],
    detailsColumns: ['type', 'status'],
    showActionMenu: true,
    renderField: (field, value, theme, row) => {
      switch (field) {
        case 'statement':
          return row?.profile_library_question?.statement || value
        case 'type':
          return row?.profile_library_question?.type || value
        default:
          return value
      }
    }
  },
  'managers-spending-imit': {
    summaryColumns: ['full_name'],
    detailsColumns: ['default_spending_limit', 'custom_spending_limit', 'remaining_spending_limit'],
    showActionMenu: true,
    renderField: (field, value) => {
      switch (field) {
        case 'date':
          return (
            value ? dayjs(value).format('MM/DD/YYYY') : 'N/A'
          )
        default:
          return value
      }
    }
  },
  payments_history: {
    summaryColumns: ['brand'],
    detailsColumns: ['token_id', 'Number', 'create_date', 'amount', 'status'],
    showActionMenu: true,
    renderField: (field, value) => {
      switch (field) {
        case 'create_date':
          return (
            value ? dayjs(value).format('MM/DD/YYYY') : 'N/A'
          )
        default:
          return value
      }
    }
  },
  rewards_history: {
    summaryColumns: ['department_demographic'],
    detailsColumns: ['created_at', 'full_name', 'event_name', 'value', 'user_rewarding_full_name'],
    showActionMenu: false,
    renderField: (field, value) => {
      switch (field) {
        case 'created_at':
          return (
            value ? dayjs(value).format('MM/DD/YYYY') : 'N/A'
          )
        default:
          return value
      }
    }
  },
  inappropriateContent: {
    summaryColumns: ['record_type', 'reporter.full_name'],
    detailsColumns: [
      'content_creator',
      'created_at',
      'has_inappropriate_text',
      'has_inappropriate_image',
      'has_inappropriate_video',
      'moderation_result'
    ],
    renderField: (field, value, row) => {
      switch (field) {
        case 'created_at': {
          return dayjs(value).format('MM/DD/YYYY')
        }
        case 'moderation_result': {
          const formattedValue = value
            ? value.replace(/_/g, ' ')
              .replace(/\b\w/g, char => char.toUpperCase())
            : ''

          return (
            <Typography
              variant="body2"
              color={
                value === 'moderation_pending' ? 'warning.main' :
                  value === 'resolved' ? 'success.main' :
                    'error.main'
              }
            >
              {formattedValue}
            </Typography>
          )
        }
        case 'content_creator':
        case 'reporter.full_name': {
          const nestedValue = field.includes('.')
            ? field.split('.').reduce((obj, key) => obj?.[key], row)
            : value
          return <Typography variant="body2">{nestedValue?.full_name || nestedValue}</Typography>
        }
        default: {
          return value;
        }
      }
    }
  },
  suggestions: {
    summaryColumns: ['description'],
    detailsColumns: ['created_at', 'user.name', 'status', 'assignee', 'replies'],
    renderField: (field, value) => {
      switch (field) {
        case 'created_at':
          return value ? dayjs(value).format('MM/DD/YYYY') : 'N/A'
        case 'user.name':
          return value?.full_name || 'Anonymous'
        default:
          return value
      }
    }
  },
  invoices: {
    summaryColumns: ['created', 'receipt_number', 'status'],
    detailsColumns: ['description', 'amount'],
    renderField: (field, value) => {
      switch (field) {
        case 'created':
          return value ? dayjs(value).format('MM/DD/YYYY') : 'N/A'
        default:
          return value
      }
    }
  },
  myRewards: {
    summaryColumns: ['created_at', 'type'],
    detailsColumns: ['description', 'user_rewarding.name', 'value', 'status', 'actions'],
    showActionMenu: false,
    renderField: (field, value) => {
      switch (field) {
        case 'created_at':
          return value ? dayjs(value).format('MM/DD/YYYY') : 'N/A'
        default:
          return value
      }
    }
  },
  rewardStorePurchases: {
    summaryColumns: ['details.order.orderNumber', 'created_at'],
    detailsColumns: ['details.order.orderItems', 'amount', 'created_at'],
    showActionMenu: false,
    renderField: (field, value) => {
      switch (field) {
        case 'created_at':
          return value ? dayjs(value).format('MM/DD/YYYY') : 'N/A'
        default:
          return value
      }
    }
  },
  takeAction: {
    summaryColumns: ['recognition.users_recognized'],
    detailsColumns: [
      'recognition.user_recognizing.name',
      'see_sparck',
      'recognition.event.title',
      'from_where'
    ],
    showActionMenu: true,
    renderField: (field, value, theme, row) => {
      const getNestedValue = (obj, path) => {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj)
      }

      const actualValue = getNestedValue(row, field)
      let usersRecognized

      switch (field) {
        case 'recognition.users_recognized':
          usersRecognized = actualValue
          if (usersRecognized?.length >= 2) {
            return <UsersListButton users={usersRecognized} />
          }
          return (usersRecognized ?? [])
            .map(user => user?.full_name)
            .join(', ')
        case 'recognition.user_recognizing.name':
          return actualValue
        case 'recognition.event.name':
          return actualValue
        case 'recognition.event.title':
          return actualValue
        case 'see_sparck':
          return row?.recognition?.has_public_access ? (
            <Button
              color='primary'
              size='small'
              sx={{ minWidth: 120, p: 0 }}
            >
              View Message
            </Button>
          ) : null
        case 'from_where':
          return actualValue
        default:
          return actualValue || value
      }
    }
  },
}

const UsersDialog = ({ open, onClose, users }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogCloseButton handleClose={onClose} />
      <DialogTitle>Recognized Users</DialogTitle>
      <DialogContent>
        <List>
          {users.map((user: any) => (
            <ListItem key={user.id} disablePadding sx={{ mb: 2 }}>
              <ListItemText primary={user.full_name} />
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  )
}

const UsersListButton = ({ users }) => {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Typography
        component="span"
        color="primary"
        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
        onClick={() => setOpen(true)}
      >
        See All
      </Typography>
      <UsersDialog
        open={open}
        onClose={() => setOpen(false)}
        users={users}
      />
    </>
  )
}

export const getAccordionConfig = (slug: string): AccordionConfig => {
  return configs[slug] || {
    summaryColumns: ['id', 'name'],
    detailsColumns: ['description']
  }
}
